/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Turning a portal's refusal into the sentence the broker needs.
 *
 * The backend refuses OLX work with a reason code -- no professional plan,
 * account not confirmed, reconnect required, incomplete property -- either as
 * an HTTP error body (`{"error": "olx_plan_required"}`, edit) or inside a 200
 * publish answer (`results.olx = {status: "error", reason}`, retry). Both end
 * up here so the toast says what is missing instead of "something failed".
 */

import axios from 'axios';

type Translate = (key: string) => string;

/** Reasons with a dedicated `publish.reason.*` sentence. */
const EXPLAINED_REASONS = new Set([
  'olx_plan_required', 'olx_account_unconfirmed', 'olx_terms_required', 'olx_reconnect_required', 'olx_no_slots',
  'olx_refused', 'olx_ad_removed', 'olx_invalid_ad', 'olx_rate_limited', 'olx_unavailable', 'olx_unexpected_error',
  'missing_zipcode', 'missing_price', 'missing_transaction', 'missing_phone', 'missing_bedrooms', 'invalid_phone', 'unsupported_category', 'no_images', 'not_connected',
  'listing_incomplete',
]);

/** A publish answer that came back 200 but refused the portal. */
export class PortalRefusedError extends Error {
  constructor(public readonly reason: string) {
    super(reason);
    this.name = 'PortalRefusedError';
  }
}

/**
 * Throws when a publish answer refused ``portal``.
 *
 * @param data - The publish route's response body.
 * @param portal - The portal that was requested.
 */
export function assertPortalAccepted(data: unknown, portal: string): void {
  const result = (data as { results?: Record<string, { status?: string; reason?: string }> } | null)
    ?.results?.[portal];
  if (result?.status === 'error') throw new PortalRefusedError(result.reason || 'publish_failed');
}

/**
 * The reason code carried by an error, from either shape.
 *
 * @param error - What the request threw.
 * @returns The code, or undefined.
 */
export function refusalReason(error: unknown): string | undefined {
  if (error instanceof PortalRefusedError) return error.reason;
  if (axios.isAxiosError(error)) {
    const code = (error.response?.data as { error?: unknown } | undefined)?.error;
    return typeof code === 'string' ? code : undefined;
  }
  return undefined;
}

/**
 * The explained sentence for a refusal, or the fallback.
 *
 * @param error - What the request threw.
 * @param fallbackKey - i18n key used for anything unexplained.
 * @param t - Translation function (properties namespace).
 * @returns The message to show.
 */
export function refusalMessage(error: unknown, fallbackKey: string, t: Translate): string {
  const reason = refusalReason(error);
  return reason && EXPLAINED_REASONS.has(reason) ? t(`publish.reason.${reason}`) : t(fallbackKey);
}
